import { useState, useEffect } from "react";
import { X, Copy, Check, Gamepad2, Search, Rocket, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export const SERVER_ID = "2c4f4137-6a4a-4845-adaa-0f4ca1fd7e81";
const WARDOGS_STEAM_APPID = "1867240";

// "How to join" modal — WARDOGS can't join from the browser, so we copy the Server ID
// and walk the player through the in-game Community Server Browser.
export const JoinModal = ({ open, onClose, serverId = SERVER_ID }) => {
  const [copied, setCopied] = useState(false);
  const [rules, setRules] = useState([]);

  useEffect(() => {
    if (!open || !serverId) return;
    api.get(`/server-rules/${serverId}`).then((r) => setRules(r.data.rules || [])).catch(() => {});
  }, [open, serverId]);

  if (!open) return null;

  const copyId = async () => {
    try { await navigator.clipboard.writeText(SERVER_ID); setCopied(true); toast.success("Server ID copied"); setTimeout(() => setCopied(false), 2000); }
    catch (e) { toast.error("Couldn't copy — select the ID and copy manually"); }
  };

  const launchGame = () => {
    window.location.href = `steam://run/${WARDOGS_STEAM_APPID}`;
    toast.message("Launching WARDOGS via Steam…");
  };

  return (
    <div data-testid="join-modal" className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm fade-in" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#13151B] border border-[#D4A94A]/40 tactical-frame hud-corners p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button data-testid="join-modal-close" onClick={onClose} className="absolute top-3 right-3 text-[#64748B] hover:text-[#F1F5F9]"><X className="w-5 h-5" /></button>

        <div className="overline mb-1">// DEPLOY TO OCE DOGS</div>
        <h2 className="font-heading text-2xl font-black uppercase flex items-center gap-2 mb-1"><Gamepad2 className="w-6 h-6 text-[#D4A94A]" /> How to Join</h2>
        <p className="font-mono text-[11px] text-[#94A3B8] mb-4">WARDOGS can't join from a browser — but it takes 10 seconds in-game with the Server ID below.</p>

        <div className="mb-5">
          <div className="overline mb-1">Your Server ID</div>
          <button data-testid="join-modal-copy" onClick={copyId} className="group w-full flex items-center justify-between gap-2 bg-[#0B0C0E] border border-[#2A2E3D] hover:border-[#D4A94A] px-3 py-2.5 tactical-frame-sm transition-colors">
            <span className="font-mono text-xs sm:text-sm text-[#F1F5F9] truncate">{SERVER_ID}</span>
            <span className={`inline-flex items-center gap-1 font-stencil font-bold uppercase text-[10px] shrink-0 ${copied ? "text-[#2ECC71]" : "text-[#D4A94A]"}`}>{copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}</span>
          </button>
          <button data-testid="join-modal-launch" onClick={launchGame} className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-[#2ECC71] hover:bg-[#33d97a] text-black font-stencil font-black uppercase text-sm py-2.5 tactical-frame-sm transition-colors">
            <Rocket className="w-4 h-4" /> Launch WARDOGS via Steam
          </button>
          <p className="font-mono text-[10px] text-[#64748B] mt-1.5">Opens the game through Steam (WARDOGS must be installed). The game can't auto-join a server from a browser, so use the Server ID above once you're in.</p>
        </div>

        <ol className="space-y-4">
          <li className="flex gap-3">
            <span className="shrink-0 w-6 h-6 flex items-center justify-center bg-[#D4A94A] text-black font-heading font-black text-sm tactical-frame-sm">1</span>
            <div>
              <div className="font-stencil font-bold uppercase text-sm text-[#F1F5F9]">Open the Community Server Browser</div>
              <p className="font-mono text-[11px] text-[#94A3B8] mt-0.5">From the <b className="text-[#F1F5F9]">Deploy</b> screen, select <b className="text-[#F1F5F9]">Community</b>.</p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="shrink-0 w-6 h-6 flex items-center justify-center bg-[#D4A94A] text-black font-heading font-black text-sm tactical-frame-sm">2</span>
            <div>
              <div className="font-stencil font-bold uppercase text-sm text-[#F1F5F9] flex items-center gap-1.5"><Search className="w-3.5 h-3.5 text-[#D4A94A]" /> Join Using Your Server ID</div>
              <p className="font-mono text-[11px] text-[#94A3B8] mt-0.5">In the Community Server Browser, click <b className="text-[#F1F5F9]">Join by ID</b> in the bottom-left corner. Paste the Server ID and click <b className="text-[#F1F5F9]">Lookup</b>.</p>
            </div>
          </li>
        </ol>

        {rules.length > 0 && (
          <div className="mt-5 pt-4 border-t border-[#2A2E3D]" data-testid="join-modal-rules">
            <div className="overline mb-2 inline-flex items-center gap-1.5"><ScrollText className="w-3 h-3 text-[#1DD65C]" /> Top server rules</div>
            <ol className="space-y-1.5">
              {rules.slice(0, 5).map((r, i) => (
                <li key={i} className="flex items-start gap-2 font-mono text-[11px] text-[#94A3B8] leading-relaxed">
                  <span className="shrink-0 text-[#1DD65C] font-bold">{i + 1}.</span><span>{r}</span>
                </li>
              ))}
            </ol>
            {rules.length > 5 && <p className="font-mono text-[10px] text-[#64748B] mt-2">+{rules.length - 5} more — see the full list on the server page.</p>}
          </div>
        )}

        <p className="font-mono text-[10px] text-[#64748B] mt-5 pt-4 border-t border-[#2A2E3D]">Tip: you can also search for <b className="text-[#94A3B8]">OCE DOGS</b> by name in the Community browser. See you on the field, operator.</p>
      </div>
    </div>
  );
};

export default JoinModal;
